import { useCallback, useMemo, useState } from 'react';
import GuestHash from '@projects/models/guestHash';
import useUpdateGuestHashMutation from '@projects/queries/useUpdateGuestHashMutation';
import useSnackbarSystem from '@ui/systems/useSnackbarSystem';
import { trackGuestLinkCopied, trackGuestLinkSettingsChanged } from 'src/services/tracker/trackers/nac';

type UseInviteLinkProps = {
	projectID: number;
	guestHash: GuestHash;
}

const useInviteLink = ( { guestHash, projectID }: UseInviteLinkProps ) => {
	const [ showSuccessMessage, setShowSuccessMessage ] = useState( false );
	const snackbarSystem = useSnackbarSystem();
	const guestLink = `${document.location.origin}/p/${guestHash.guestHash}`;
	const { mutate: updateGuestHash } = useUpdateGuestHashMutation( {
		guestHashID: guestHash.id,
		projectID
	} );

	const copyShareLink = useCallback( () => {
		navigator.clipboard.writeText( guestLink );
		setShowSuccessMessage( true );
		trackGuestLinkCopied( { projectID, role: guestHash.role.id } );
		setTimeout( () => setShowSuccessMessage( false ), 2000 );
	}, [ guestLink, setShowSuccessMessage ] );

	const boxStyles = useMemo( () => ( {
		borderRadius: 4,
		backgroundColor: guestHash.enabled ? 'var(--color-primary-subtle)' : 'var(--color-bg-surface)',
		border: guestHash.enabled ? '1px solid var(--color-primary-base)' : '1px solid var(--color-outline-default)'
	} ), [ guestHash.enabled ] );

	const onLinkEnabledToggled = useCallback( () => {
		const newEnabledState = !guestHash.enabled;

		updateGuestHash( newEnabledState, {
			onSuccess: () => {
				trackGuestLinkSettingsChanged( { projectID, enabled: newEnabledState, role: guestHash.role.id } );

				if ( !newEnabledState ) {
					snackbarSystem.showInfoMessage( {
						title: 'Link disabled',
						description: 'Only previously invited people with accounts will be able to access. '
							+ 'You can manage members access from People\'s tab.'
					} );
				}
			}
		} );
	}, [ guestHash.enabled, updateGuestHash, snackbarSystem ] );

	return {
		showSuccessMessage,
		copyShareLink,
		boxStyles,
		onLinkEnabledToggled
	};
};

export default useInviteLink;
